// =============================================================================
// Blocos por categoria do Painel do Gestor — o gasto que não chegou à linha
//
// Os cartões do Painel somam o realizado inteiro do centro de custo. Os blocos
// por categoria liam só as LINHAS orçamentárias, então o que não caiu em linha
// não aparecia neles — e o gestor via nas categorias uma fração do que o cartão
// mostrava, sem aviso nenhum. "Não orçado" é um dos sete estados do AF-01; ele
// precisa aparecer onde o gestor olha.
//
// Aqui só se junta e se classifica o que a API já agregou. Nenhuma soma de
// lançamento acontece no front.
// =============================================================================

import type { LinhaMes, OrcadoRealizadoLinha, SemLinhaMes } from "./api.ts";

/** Um item dos blocos por categoria: a linha orçamentária, ou o gasto fora dela. */
export type ItemCategoria = LinhaMes & {
  /** Preenchido quando o item é gasto que não chegou a nenhuma linha. É a
   *  coordenada do drilldown, porque não existe `linha_id` para abrir. */
  foraDaLinha: SemLinhaMes | null;
  /** Chave para a tela. `categoria_id` não serve: a mesma categoria pode ter
   *  linha orçada num mês e gasto sem orçamento em outro do mesmo período, e
   *  uma categoria pode ter várias linhas (uma por fornecedor). */
  chave: string;
};

/**
 * Junta as linhas do centro de custo com o gasto que ficou fora delas.
 *
 * O gasto fora da linha vira "Não orçado" quando a categoria não tem orçamento
 * no mês, e "Aguardando alocação" quando tem e o lançamento ainda não foi
 * atribuído a uma linha.
 *
 * Ordem por nome de categoria, com a linha orçada antes do gasto fora dela na
 * mesma categoria — a leitura natural é "o que estava previsto, e o que passou
 * por fora".
 */
export function itensDeCategoria(
  linhas: LinhaMes[],
  semLinha: SemLinhaMes[],
  centroCustoId: string,
): ItemCategoria[] {
  const orcadas: ItemCategoria[] = linhas
    .filter((l) => l.centro_custo_id === centroCustoId)
    .map((l, i) => ({
      ...l,
      foraDaLinha: null,
      chave: `linha:${l.linha_id ?? `${l.categoria_id}:${l.competencia}:${i}`}`,
    }));

  const fora: ItemCategoria[] = semLinha
    .filter((n) => n.centro_custo_id === centroCustoId)
    .map((n) => ({
      linha_id: null,
      exercicio_ano: Number(n.competencia.slice(0, 4)),
      centro_custo_id: n.centro_custo_id,
      centro_custo_codigo: n.centro_custo_codigo,
      centro_custo_nome: n.centro_custo_nome,
      categoria_id: n.categoria_id ?? "",
      categoria_nome: n.categoria_nome,
      competencia: n.competencia,
      tipo: "",
      fornecedor_id: null,
      fornecedor_nome: null,
      orcado_centavos: "0",
      realizado_centavos: n.realizado_centavos,
      comprometido_centavos: n.comprometido_centavos,
      disponivel_centavos: n.disponivel_centavos,
      pct_execucao: "0",
      status: n.tem_orcamento ? "aguardando_alocacao" : "nao_orcado",
      foraDaLinha: n,
      chave: `fora:${n.tem_orcamento ? "com" : "sem"}-orcamento:${n.categoria_id ?? "sem-categoria"}`,
    }));

  const nome = (a: ItemCategoria, b: ItemCategoria) =>
    a.categoria_nome.localeCompare(b.categoria_nome, "pt-BR");
  // `sort` é estável: dentro do mesmo nome, as orçadas (que vêm antes) ficam antes.
  return [...orcadas, ...fora].sort(nome);
}

// ── mapa de calor categoria × mês ───────────────────────────────────────────

export type CelulaHeatmap =
  | { tipo: "vazio" }
  | { tipo: "pct"; valor: number }
  /** Gastou e não tinha orçamento no mês. Não é "0%" nem "∞%": é outra coisa. */
  | { tipo: "sem_orcamento" };

export function celulaHeatmap(orcado: bigint, realizado: bigint): CelulaHeatmap {
  if (orcado > 0n) return { tipo: "pct", valor: Number((realizado * 10000n) / orcado) / 100 };
  if (realizado > 0n) return { tipo: "sem_orcamento" };
  return { tipo: "vazio" };
}

/**
 * As linhas do mapa de calor de UM centro de custo, de janeiro até `ateMes`.
 * Com `centroCustoId` nulo, de todos os que a pessoa vê, somados por
 * categoria — a aba Despesa da Visão financeira (pedido de 27/10).
 *
 * Parte do orçado × realizado por coordenada (044), e não da execução por
 * linha: é o que faz a categoria que gastou sem orçamento aparecer. Categoria
 * sem nada em nenhum mês até ali (só comprometido, ou orçada só no futuro)
 * fica de fora, porque a linha seria toda de traços.
 */
export function linhasHeatmap(
  dados: OrcadoRealizadoLinha[],
  centroCustoId: string | null,
  ateMes: number,
): { chave: string; nome: string; celulas: CelulaHeatmap[] }[] {
  const mapa = new Map<string, { nome: string; orcado: bigint[]; realizado: bigint[] }>();
  for (const d of dados) {
    if (centroCustoId !== null && d.centro_custo_id !== centroCustoId) continue;
    const i = Number(d.competencia.slice(5, 7)) - 1;
    if (i < 0 || i >= ateMes) continue;
    const chave = d.categoria_id ?? "sem-categoria";
    const atual = mapa.get(chave) ?? {
      nome: d.categoria_nome,
      orcado: Array.from({ length: ateMes }, () => 0n),
      realizado: Array.from({ length: ateMes }, () => 0n),
    };
    atual.orcado[i] = (atual.orcado[i] ?? 0n) + BigInt(d.orcado_centavos);
    atual.realizado[i] = (atual.realizado[i] ?? 0n) + BigInt(d.realizado_centavos ?? "0");
    mapa.set(chave, atual);
  }

  return [...mapa.entries()]
    .map(([chave, v]) => ({
      chave,
      nome: v.nome,
      celulas: v.orcado.map((o, i) => celulaHeatmap(o, v.realizado[i] ?? 0n)),
    }))
    .filter((l) => l.celulas.some((c) => c.tipo !== "vazio"))
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
}
